import { z } from 'zod';

/** The tag of the user's assistant conversations, which the sidebar shows above the others. */
export const ASSISTANT_TAG = 'assistant';

/**
 * Each persona of the call plugin (an avatar) is an assistant of its own: its conversation carries
 * `persona:<id>` beside `ASSISTANT_TAG`. Without a call plugin, the assistant has none.
 */
const PERSONA_TAG_PREFIX = 'persona:';
export const assistantPersonaTag = (persona: string) => `${PERSONA_TAG_PREFIX}${persona}`;
/** The persona an assistant conversation's tags name; null for the assistant without one. */
export function assistantPersonaOf(tags: readonly string[] | null | undefined): string | null {
  const tag = tags?.find((entry) => entry.startsWith(PERSONA_TAG_PREFIX));
  return tag === undefined ? null : tag.slice(PERSONA_TAG_PREFIX.length);
}

/** Every persona a conversation's tags name: an assistant's one, a group's several. */
export function assistantPersonasOf(tags: readonly string[] | null | undefined): string[] {
  return (tags ?? [])
    .filter((entry) => entry.startsWith(PERSONA_TAG_PREFIX))
    .map((entry) => entry.slice(PERSONA_TAG_PREFIX.length));
}

/** A persona's id, as the call plugin names it (`callPersonaSchema`). */
const personaIdSchema = z.string().trim().min(1).max(128);
/** An assistant's id (`/api/assistants`). */
const assistantIdSchema = z.uuid();

/**
 * The tag of a group conversation of personas (`group` collaboration), whose members answer from
 * their own assistant conversations, tagged with each of them.
 */
export const ASSISTANT_GROUP_TAG = 'assistant-group';
/** Personas a group takes at most. */
export const MAX_GROUP_PERSONAS = 4;
/**
 * The tag of a group conversation's member, one of the user's assistants (docs/adr-assistants.md):
 * a room is keyed on its assistants. Rooms made before name their members' avatars
 * (`assistantPersonaTag`).
 */
const MEMBER_TAG_PREFIX = 'member:';
export const assistantMemberTag = (assistant: string) => `${MEMBER_TAG_PREFIX}${assistant}`;
/** The assistants a group conversation's tags name. */
export function assistantMembersOf(tags: readonly string[] | null | undefined): string[] {
  return (tags ?? [])
    .filter((entry) => entry.startsWith(MEMBER_TAG_PREFIX))
    .map((entry) => entry.slice(MEMBER_TAG_PREFIX.length));
}

/**
 * A group conversation: its room, and the assistants in it, as the user sees them. A call makes
 * one of assistants (2 to 4: "ajoute Marie"), found again for the same ones; `GET
 * /api/assistant/groups/:id` shows a room and `POST` sends its commands (`@crowdex/groups`).
 */
export type AssistantGroupView = {
  conversation: string;
  title: string;
  /** Its assistants, each with its avatar (null: none), in the order they joined. */
  members: { id: string; title: string; avatar: string | null }[];
  /** Older clients' view: the members that have an avatar, by their avatar. */
  personas: { id: string; title: string }[];
};

/**
 * The `assistant` tools code mode runs without asking the user: those that only read, `show` and
 * `call_user` (what the user asked of their assistant, which its turns woken by events must do
 * too). The server marks them, and code mode trusts this list, not the marks.
 */
export const ASSISTANT_UNASKED_TOOLS: readonly string[] = [
  'overview',
  'list_conversations',
  'read_conversation',
  'list_agents',
  'show',
  'call_user',
  'follow_conversation',
];

/** A persona the user can talk to, and its assistant conversation (null before its first turn). */
export type AssistantPersonaView = {
  id: string;
  title: string;
  conversation: string | null;
  /** Its voice (an id of the user's voice provider), which a group call speaks its words in. */
  voice?: string;
};

/**
 * An assistant a call speaks as (docs/adr-assistants.md): its name, colour and avatar (whose face,
 * voice and personality the call takes; null: the default voice), and its channel, where the
 * call is kept (null before its first turn). `voice` is the one a group call speaks its words in.
 */
export type AssistantCallMember = AssistantPersonaView & {
  color: string;
  home: boolean;
  avatar: string | null;
};

/**
 * `GET /api/assistant`: the selected persona's assistant conversation (null before its first
 * turn), that persona (null without a call plugin) and every persona the user can talk to.
 * `POST` with `{ persona }` selects one, and answers the same.
 */
export type AssistantView = {
  conversation: string | null;
  persona: string | null;
  personas: AssistantPersonaView[];
};
export const assistantSelectRequestSchema = z.object({ persona: personaIdSchema }).strict();

/** What was said in the call, by the user or the call's voice. */
export const assistantVoiceLineSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string().trim().min(1).max(4_000),
  /** When it was said, in milliseconds from the call's start. */
  atMs: z.number().int().min(0).max(86_400_000).optional(),
});
export const assistantVoiceSchema = z.array(assistantVoiceLineSchema).max(40);

/**
 * The user's live call with their assistant, which the server runs: the page keeps the media (its
 * microphone, the call's audio) and shows the call; the server drives the voice (on the call's
 * sideband, or as participants of the call's room), hands tasks to the assistant, speaks its
 * replies, and keeps what the user asked going after they hang up.
 */

/**
 * A page's media offer for a call's session (its WebRTC SDP): the server answers it, or names
 * the call's room for the page to join instead.
 */
const callOfferSchema = { sdp: z.string().min(1).max(1_000_000) };

/**
 * `POST /api/assistant/calls`: starts a call with `assistant` (the home assistant otherwise), or
 * answering the assistant's `ring` (then with the assistant that called, saying first why).
 * `persona`, older clients' way, names the assistant that avatar serves. Answers
 * `AssistantCallStarted`.
 */
export const assistantCallStartSchema = z
  .object({
    ...callOfferSchema,
    /** The user's language (BCP 47), which the call speaks and listens in. */
    language: z.string().trim().min(2).max(35).optional(),
    assistant: assistantIdSchema.optional(),
    persona: personaIdSchema.optional(),
    ring: z.uuid().optional(),
    /** The conversation on screen, which the call is about (not an assistant's). */
    openConversation: z.uuid().optional(),
    /** The folder on screen, where the assistant starts when the open conversation is new. */
    openWorkspace: z.string().trim().min(1).max(4_096).optional(),
  })
  .strict();
export type AssistantCallStart = z.input<typeof assistantCallStartSchema>;

/**
 * `POST /api/assistant/calls/:call/session`: the page's offer for the call's next session, once
 * the server asked for one (`renegotiate`: handed over to another persona, or a group call).
 * Answers `{ answer }`.
 */
export const assistantCallSessionSchema = z.object(callOfferSchema).strict();

const namesOne = (command: { assistant?: string | undefined; persona?: string | undefined }) =>
  (command.assistant === undefined) !== (command.persona === undefined);
const NAMES_ONE = 'Name the assistant, or the persona it speaks as.';

/** `POST /api/assistant/calls/:call`: what the user does in the call. */
export const assistantCallCommandSchema = z.discriminatedUnion('type', [
  /** Hangs up: what they asked goes on, the assistant's replies kept in its conversation. */
  z.object({ type: z.literal('hangup') }).strict(),
  /** The conversation now on screen (not an assistant's), which the call is about from now on. */
  z
    .object({
      type: z.literal('focus'),
      conversation: z.uuid().nullable(),
      openWorkspace: z.string().trim().min(1).max(4_096).nullable().optional(),
    })
    .strict(),
  /** Typed in the assistant's conversation (the group's room in a group call): its reply is said. */
  z.object({ type: z.literal('type'), text: z.string().trim().min(1).max(100_000) }).strict(),
  /** Answers the request the call asks about. */
  z.object({ type: z.literal('answer'), approved: z.boolean() }).strict(),
  /** Hands the call over to another assistant (`persona`: the one that avatar serves). */
  z
    .object({
      type: z.literal('switch'),
      assistant: assistantIdSchema.optional(),
      persona: personaIdSchema.optional(),
    })
    .strict()
    .refine(namesOne, NAMES_ONE),
  /** Brings an assistant in: a group call with the ones on the line. */
  z
    .object({
      type: z.literal('invite'),
      assistant: assistantIdSchema.optional(),
      persona: personaIdSchema.optional(),
    })
    .strict()
    .refine(namesOne, NAMES_ONE),
]);
export type AssistantCallCommand = z.input<typeof assistantCallCommandSchema>;

/** A step of an assistant turn, as the call shows it. */
export type AssistantCallStep = {
  id: string;
  label: string;
  state: 'running' | 'waiting' | 'done' | 'failed';
};
/** A conversation the assistant started or sent work to during the call, which it follows. */
export type AssistantCallFollowed = { conversation: string; label: string };
/** What a turn waits on the user for, which the call asks about and can answer. */
export type AssistantCallRequestView = {
  conversation: string;
  turn: string;
  request: string;
  label: string;
};

/** The call as the server runs it, which its page shows. */
export type AssistantCallView = {
  /** The assistant works on what the user asked. */
  working: boolean;
  /** What it does now. */
  activity: string | null;
  /** The steps of its latest turn. */
  steps: AssistantCallStep[];
  /** The conversations it worked with during the call, latest first. */
  followed: AssistantCallFollowed[];
  request: AssistantCallRequestView | null;
  /** The channel of the assistant the call speaks as (null before its first turn). */
  assistant: string | null;
  /** The assistant the call speaks as; null before the call knows it. */
  speaker: string | null;
  /** The user's assistants, home first: those it can be handed over to, or bring in. */
  assistants: AssistantCallMember[];
  /** Older clients' view: the speaker's avatar, and the assistants that have one, by it. */
  persona: string | null;
  personas: AssistantPersonaView[];
  /** A group call: its group, who speaks now, and who prepares an answer. */
  group: AssistantGroupView | null;
  groupSpeaker: string | null;
  groupThinking: string | null;
  error: string | null;
};

/** `POST /api/assistant/calls`'s answer: the call, its session's answer to the page's offer. */
export type AssistantCallStarted = {
  call: string;
  /** `realtime.v1` `call.create`'s answer, or the call's room to join (`RealtimeAnswer`). */
  answer: Record<string, unknown>;
  view: AssistantCallView;
  /** Answering a ring: what it was about (null: it rang no more, and no call started). */
  ring: AssistantRingAnswer | null;
};

/**
 * `GET /api/assistant/calls/:call/stream` (server-sent events, one JSON message each): what the
 * page shows and does for the call.
 */
export type AssistantCallMessage =
  | { type: 'view'; view: AssistantCallView }
  /** The page starts the call's next session (`POST .../session`): its voice goes on there. */
  | { type: 'renegotiate'; reason: 'handover' | 'group' }
  | { type: 'ended'; reason: 'hangup' | 'closed' | 'failed'; error?: string };

/**
 * The assistant calling the user (its `call_user` tool, or an event they chose to be called
 * about): every page and device rings until one answers, they decline or it stops ringing.
 */
export type AssistantRingView = {
  ring: string;
  /** What the assistant calls about, which the call says first. */
  reason: string;
  /**
   * What the call has to tell or show beyond the reason (a report of the work, what it made):
   * not shown on the ring, given to the call when it is answered.
   */
  details: string | null;
  /** A file of the conversation's workspace the call shows when it is answered (the result). */
  file: string | null;
  /** The conversation it is about, when there is one. */
  conversation: string | null;
  /** The assistant that calls (the home one unless another called): the call speaks as it. */
  assistant?: string | null;
  /** Its avatar, for older clients. */
  persona: string | null;
  /** When it stops ringing, missed. */
  until: string;
};

/** The longest `details` a ring carries: a spoken report, not a document. */
export const RING_DETAILS_LENGTH = 6_000;

/** `GET /api/assistant/ring`: the ring going on now, if any. */
export type AssistantRingStatus = { ring: AssistantRingView | null };

/**
 * `POST /api/assistant/ring`: a page answers the ring (then starts the call) or declines it. The
 * first page to answer has it; the others are told it was answered.
 */
export const assistantRingRequestSchema = z
  .object({ ring: z.uuid(), answer: z.boolean() })
  .strict();
export type AssistantRingAnswer =
  | {
      status: 'answered';
      reason: string;
      details: string | null;
      file: string | null;
      conversation: string | null;
      assistant?: string | null;
      persona: string | null;
    }
  | { status: 'declined' }
  /** It stopped ringing: answered elsewhere, declined, missed, or merged into a newer ring. */
  | { status: 'gone' };

/**
 * `POST /api/assistant/turns`: a turn of the user's assistant, which the call delegates to. The
 * answer is the turn's UI message stream, its conversation in `x-crowdex-conversation-id`; 409
 * only when the assistant already runs a turn.
 */
export const assistantTurnRequestSchema = z
  .object({
    text: z.string().trim().min(1).max(100_000),
    /** The conversation on the user's screen: the assistant's context, and the place of its first turn. */
    openConversation: z.uuid().optional(),
    /**
     * The folder the user works in on screen, where the assistant's first turn starts when the
     * open conversation is not saved yet (a new one).
     */
    openWorkspace: z.string().trim().min(1).max(4_096).optional(),
    /**
     * The call's own exchanges since the last turn, which the assistant did not hear: kept in its
     * conversation before the turn, and told to its agent.
     */
    voice: assistantVoiceSchema.default([]),
    /** The user hears the reply in a call. */
    call: z.boolean().default(false),
    /** The call is one the user answered: what it was about, and the conversation it named. */
    answered: z
      .object({
        reason: z.string().trim().min(1).max(500),
        details: z.string().max(RING_DETAILS_LENGTH).nullable().default(null),
        conversation: z.uuid().nullable(),
      })
      .optional(),
    /** What its agent may do without asking; the user's choice for Live (`permissions`) otherwise. */
    permissionMode: z.enum(['allow-reads', 'allow-edits', 'allow-all']).optional(),
    /** The assistant that answers (the one the call speaks as); the home assistant otherwise. */
    assistant: assistantIdSchema.optional(),
    /** Older clients' way: the assistant that avatar serves. */
    persona: personaIdSchema.optional(),
  })
  .strict();
export type AssistantTurnRequest = z.input<typeof assistantTurnRequestSchema>;

export const ASSISTANT_CONVERSATION_HEADER = 'x-crowdex-conversation-id';

/**
 * `POST /api/assistant/tools`: one of the assistant's tools, run on this server for a conversation
 * that lives here: a machine linked to Crowdex Cloud runs them on the cloud this way.
 */
export const assistantToolRequestSchema = z
  .object({
    name: z.enum([
      'overview',
      'list_conversations',
      'read_conversation',
      'start_conversation',
      'send_to_conversation',
      'answer_request',
      'show',
      'stop_conversation',
      'list_agents',
      'mark_read',
      'rename_conversation',
      'archive_conversation',
      'call_user',
      'follow_conversation',
    ]),
    arguments: z.record(z.string(), z.unknown()).default({}),
  })
  .strict();
